import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageSquare, Bot, FileText, Download, Printer, Globe, Slack, MessageCircle, Mail, Smartphone, Code2, ChevronRight, ChevronDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getAuthToken } from "@/lib/queryClient";
import { PageShell } from "@/components/page-shell";
import type { Chatbot } from "@shared/schema";

// Shape of GET /api/conversations rows (ConversationsController.ConvDto).
// This endpoint is org-scoped server-side (same tenant scoping as Tenant
// History): tenants only ever receive conversations belonging to their own
// org's chatbots, both guest and staff sessions.
import { aiSourceLabel } from "@/lib/ai-execution-metadata";

interface ConvRow {
  id: string;
  chatbotId: string;
  sessionId: string;
  userName: string | null;
  userEmail: string | null;
  channel: string | null;
  status: string | null;
  messageCount: number;
  createdAt: string | null;
  type: string;
}

export default function ConversationsPage() {
  const { data: chatbots, isLoading: botsLoading, error: botsError } = useQuery<Chatbot[]>({
    queryKey: ["/api/chatbots"],
  });
  // One org-scoped fetch for ALL conversations — the per-bot
  // /api/chatbots/:id/conversations endpoint is platform-admin-only and
  // would return 403 for tenant admins (which left this page empty).
  const { data: conversations, isLoading: convosLoading, error: convosError } = useQuery<ConvRow[]>({
    queryKey: ["/api/conversations"],
  });

  const isLoading = botsLoading || convosLoading;
  const error = botsError || convosError;

  // Accordion: only one bot section open at a time. null = all collapsed.
  const [expandedBotId, setExpandedBotId] = useState<string | null>(null);

  const toggle = (botId: string) =>
    setExpandedBotId((prev) => (prev === botId ? null : botId));

  // Group by chatbot; keep the bot's display order from /api/chatbots.
  const byBot = new Map<string, ConvRow[]>();
  for (const c of conversations || []) {
    const list = byBot.get(c.chatbotId) || [];
    list.push(c);
    byBot.set(c.chatbotId, list);
  }
  const groups = (chatbots || [])
    .map((bot) => ({ bot, convos: byBot.get(bot.id) || [] }))
    .filter((g) => g.convos.length > 0);

  return (
    <PageShell
      title="Conversations"
      titleTestId="text-conversations-title"
      subtitle="View chat conversations across all your chatbots."
    >
      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-5 w-32 mb-2" />
                <Skeleton className="h-4 w-48" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16" data-testid="conversations-error">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <MessageSquare className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-lg font-semibold mb-1">Couldn't load conversations</h2>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              {(error as Error)?.message || "Something went wrong. Please try again."}
            </p>
          </CardContent>
        </Card>
      ) : groups.length > 0 ? (
        <div className="space-y-3">
          {groups.map(({ bot, convos }) => (
            <ConversationGroup
              key={bot.id}
              chatbot={bot}
              conversations={convos}
              isOpen={expandedBotId === bot.id}
              onToggle={() => toggle(bot.id)}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16" data-testid="conversations-empty">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <MessageSquare className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-lg font-semibold mb-1">No conversations yet</h2>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              Conversations will appear here once users start chatting with your bots
            </p>
          </CardContent>
        </Card>
      )}
    </PageShell>
  );
}

function TranscriptDialog({ conversationId, open, onClose }: { conversationId: string; open: boolean; onClose: () => void }) {
  const { toast } = useToast();
  const { data: transcript, isLoading } = useQuery<any>({
    queryKey: ["/api/conversations", conversationId, "transcript"],
    enabled: open && !!conversationId,
  });

  const handleDownload = async () => {
    try {
      const token = getAuthToken();
      const res = await fetch(`/api/conversations/${conversationId}/email-transcript`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `transcript-${conversationId}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: "Transcript downloaded" });
    } catch (err: any) {
      toast({ title: "Download failed", description: err.message, variant: "destructive" });
    }
  };

  const handlePrint = () => {
    if (!transcript) return;
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    let html = `<html><head><title>Transcript - ${transcript.chatbot?.name || "Chat"}</title><style>body{font-family:sans-serif;padding:20px;max-width:800px;margin:0 auto}h1{font-size:18px}.meta{color:#666;font-size:13px;margin-bottom:16px}.msg{padding:8px 12px;margin:4px 0;border-radius:8px}.user{background:#e3f2fd;text-align:right}.bot{background:#f5f5f5}.time{font-size:11px;color:#999}.rating{margin-top:16px;padding:12px;background:#fff3e0;border-radius:8px}@media print{body{padding:0}}</style></head><body>`;
    html += `<h1>${transcript.chatbot?.name || "Chat"} - Transcript</h1>`;
    html += `<div class="meta">Session: ${transcript.conversation?.sessionId || ""}<br>`;
    if (transcript.conversation?.userName) html += `User: ${transcript.conversation.userName}<br>`;
    if (transcript.conversation?.userEmail) html += `Email: ${transcript.conversation.userEmail}<br>`;
    html += `Started: ${transcript.conversation?.startedAt ? new Date(transcript.conversation.startedAt).toLocaleString() : "N/A"}</div>`;
    transcript.messages?.forEach((m: any) => {
      const cls = m.role === "user" ? "user" : "bot";
      const time = m.timestamp ? new Date(m.timestamp).toLocaleTimeString() : "";
      html += `<div class="msg ${cls}"><span class="time">${time}</span><br>${m.content}</div>`;
    });
    if (transcript.rating) {
      html += `<div class="rating">Rating: ${transcript.rating.rating}/5`;
      if (transcript.rating.feedback) html += `<br>Feedback: ${transcript.rating.feedback}`;
      html += `</div>`;
    }
    html += `</body></html>`;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />Conversation Transcript
          </DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : transcript ? (
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground space-y-1">
              <p>Session: {transcript.conversation?.sessionId}</p>
              {transcript.conversation?.userName && <p>User: {transcript.conversation.userName}</p>}
              {transcript.conversation?.userEmail && <p>Email: {transcript.conversation.userEmail}</p>}
              <p>Started: {transcript.conversation?.startedAt ? new Date(transcript.conversation.startedAt).toLocaleString() : "N/A"}</p>
            </div>
            <div className="space-y-2">
              {transcript.messages?.map((m: any, i: number) => (
                <div key={i} className={`p-3 rounded-md text-sm ${m.role === "user" ? "bg-primary/10 ml-8" : "bg-muted/50 mr-8"}`} data-testid={`transcript-message-${i}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs font-medium">{m.role === "user" ? "User" : transcript.chatbot?.name || "Bot"}</span>
                    <span className="text-[10px] text-muted-foreground">{m.timestamp ? new Date(m.timestamp).toLocaleTimeString() : ""}</span>
                  </div>
                  <p className="whitespace-pre-wrap">{m.content}</p>
                  {m.role === "assistant" && m.aiCredentialSource && (
                    <div className="mt-2 text-xs text-muted-foreground" data-testid="ai-execution-metadata">
                      <p>AI source: {aiSourceLabel(m)}</p>
                      {m.aiProvider && <p>Provider: {m.aiProvider} · Model: {m.aiModel || "Provider default"}</p>}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {transcript.rating && (
              <div className="p-3 rounded-md bg-yellow-50 dark:bg-yellow-950/30 text-sm">
                <p className="font-medium">Rating: {transcript.rating.rating}/5</p>
                {transcript.rating.feedback && <p className="text-muted-foreground mt-1">{transcript.rating.feedback}</p>}
              </div>
            )}
            <div className="flex items-center gap-2 pt-2">
              <Button variant="outline" onClick={handleDownload} data-testid="button-download-transcript">
                <Download className="w-4 h-4 mr-1" />Download
              </Button>
              <Button variant="outline" onClick={handlePrint} data-testid="button-print-transcript">
                <Printer className="w-4 h-4 mr-1" />Print
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Unable to load transcript.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ConversationGroup({
  chatbot,
  conversations,
  isOpen,
  onToggle,
}: {
  chatbot: Chatbot;
  conversations: ConvRow[];
  isOpen: boolean;
  onToggle: () => void;
}) {
  const [selectedConvo, setSelectedConvo] = useState<string | null>(null);

  if (!conversations || conversations.length === 0) return null;

  const Chevron = isOpen ? ChevronDown : ChevronRight;

  return (
    <>
      <Card className="overflow-hidden">
        {/* ── Clickable header — always visible ── */}
        <CardHeader
          className="cursor-pointer select-none hover:bg-muted/40 transition-colors"
          onClick={onToggle}
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          aria-controls={`convos-${chatbot.id}`}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onToggle()}
          data-testid={`group-header-${chatbot.id}`}
        >
          <div className="flex items-center gap-3">
            <Chevron className="w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-200" aria-hidden="true" />
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
              style={{ backgroundColor: chatbot.primaryColor || "#0c3547" }}
            >
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div>
              <CardTitle className="text-sm">{chatbot.name}</CardTitle>
              <p className="text-xs text-muted-foreground">
                {conversations.length} conversation{conversations.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
        </CardHeader>

        {/* ── Collapsible conversation list ── */}
        <div
          id={`convos-${chatbot.id}`}
          className="grid transition-all duration-300 ease-in-out"
          style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
        >
          <div className="overflow-hidden">
            <CardContent className="pt-0">
              <div className="space-y-2">
                {conversations.slice(0, 10).map((conv) => (
                  <button
                    key={conv.id}
                    className="w-full text-left flex flex-wrap items-center justify-between gap-x-2 gap-y-2 p-3 rounded-md bg-muted/50 cursor-pointer hover-elevate"
                    onClick={() => setSelectedConvo(conv.id)}
                    aria-label={`View transcript for ${conv.userName || conv.userEmail || `session ${conv.sessionId.substring(0, 8)}`}`}
                    data-testid={`button-view-transcript-${conv.id}`}
                  >
                    <div className="flex items-start sm:items-center gap-2 min-w-0 basis-full sm:basis-0 sm:flex-1">
                      <MessageSquare className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5 sm:mt-0" aria-hidden="true" />
                      <div className="min-w-0">
                        <p
                          className="text-sm font-medium break-words line-clamp-2 sm:line-clamp-1"
                          title={conv.userName || conv.userEmail || `Session: ${conv.sessionId}`}
                        >
                          {conv.userName || conv.userEmail || `Session: ${conv.sessionId.substring(0, 16)}...`}
                        </p>
                        <p className="text-xs text-muted-foreground break-words">
                          {conv.createdAt ? new Date(conv.createdAt).toLocaleString() : "—"}
                          {" · "}
                          {conv.messageCount} msg{conv.messageCount !== 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap" aria-hidden="true">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <Badge variant={conv.type === "staff" ? "default" : "secondary"} className="text-[10px] capitalize">
                        {conv.type || "guest"}
                      </Badge>
                      <ChannelBadge channel={conv.channel || "web"} />
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </div>
        </div>
      </Card>

      {selectedConvo && (
        <TranscriptDialog conversationId={selectedConvo} open={!!selectedConvo} onClose={() => setSelectedConvo(null)} />
      )}
    </>
  );
}

// Compact icon + label badge showing which channel a conversation came from.
// Defaults to "web" so legacy rows pre-Phase-2 still render sensibly.
function ChannelBadge({ channel }: { channel: string }) {
  const map: Record<string, { icon: typeof Globe; label: string }> = {
    web: { icon: Globe, label: "Web" },
    slack: { icon: Slack, label: "Slack" },
    teams: { icon: MessageCircle, label: "Teams" },
    sms: { icon: Smartphone, label: "SMS" },
    email: { icon: Mail, label: "Email" },
    mobile: { icon: Smartphone, label: "Mobile" },
    api: { icon: Code2, label: "API" },
  };
  const entry = map[channel] || { icon: Globe, label: channel };
  const Icon = entry.icon;
  return (
    <Badge variant="secondary" className="text-[10px] gap-1" data-testid={`badge-channel-${channel}`}>
      <Icon className="w-3 h-3" />
      {entry.label}
    </Badge>
  );
}
